import { useCallback, useMemo } from 'react';

/**
 * Prev/next navigation over an indexed list where the ends are hard boundaries (no wrap).
 *
 * The returned methods take `currentIndex` as an argument (rather than the hook capturing it)
 * so the memoized callbacks don't invalidate every time the selected index changes — the hook
 * only depends on `totalCount` and `onSelect`.
 */
interface SelectRelativeOptions {
  /** Total number of items in the list. When 0, all methods are effectively no-ops. */
  totalCount: number;
  /** Called with the new index the caller should select. */
  onSelect: (index: number) => void;
}

export interface SelectRelativeResult {
  /** Advance the selection by 1, unless already at the last item. */
  onForward: (currentIndex: number) => void;
  /** Retreat the selection by 1, unless already at the first item. */
  onBackward: (currentIndex: number) => void;
  /** True when the caller is already at the last item — disable the forward affordance. */
  forwardDisabled: (currentIndex: number) => boolean;
  /** True when the caller is already at the first item — disable the backward affordance. */
  backwardDisabled: (currentIndex: number) => boolean;
}

/**
 * @example
 *   const selectRelative = useSelectRelative({
 *     totalCount: items.length,
 *     onSelect: (i) => setSelectedId(items[i].id)
 *   });
 *   // In a nav control:
 *   <IconButton disabled={selectRelative.backwardDisabled(currentIndex)}
 *               onClick={() => selectRelative.onBackward(currentIndex)} />
 */
export const useSelectRelative = ({
  totalCount,
  onSelect
}: SelectRelativeOptions): SelectRelativeResult => {
  const forwardDisabled = useCallback(
    (currentIndex: number) => currentIndex >= totalCount - 1,
    [totalCount]
  );
  const backwardDisabled = useCallback((currentIndex: number) => currentIndex <= 0, []);
  const onForward = useCallback(
    (currentIndex: number) => {
      if (forwardDisabled(currentIndex)) return;
      onSelect(currentIndex + 1);
    },
    [forwardDisabled, onSelect]
  );
  const onBackward = useCallback(
    (currentIndex: number) => {
      if (backwardDisabled(currentIndex)) return;
      onSelect(currentIndex - 1);
    },
    [backwardDisabled, onSelect]
  );
  return useMemo(
    () => ({ onForward, onBackward, forwardDisabled, backwardDisabled }),
    [onForward, onBackward, forwardDisabled, backwardDisabled]
  );
};
